import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ddpsAPI } from '../services/api';
import Table from '../components/Table';
import Button from '../components/Button';
import SearchableSelect from '../components/SearchableSelect';
import Modal, { ConfirmModal } from '../components/Modal';
import { TrashIcon, EyeIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const DDPListPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const openDdpFromNavHandledRef = useRef(null);
  const { isAdmin, user } = useAuth();
  const [ddps, setDdps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ddpToDelete, setDdpToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedDdp, setSelectedDdp] = useState(null);
  const [showDdpModal, setShowDdpModal] = useState(false);
  const [ddpModalLoading, setDdpModalLoading] = useState(false);
  const [completingId, setCompletingId] = useState(null);

  const fetchDdps = useCallback(() => {
    setLoading(true);
    ddpsAPI.listForOperator()
      .then((list) => setDdps(Array.isArray(list) ? list : []))
      .catch((err) => {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || t('common.error'));
        setDdps([]);
      })
      .finally(() => setLoading(false));
  // Intentionally empty deps: fetch runs once on mount and after delete; toast/t in catch are safe to be stale
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDdps();
  }, [fetchDdps]);

  // Open DDP information modal when navigating from another page (e.g. load details)
  useEffect(() => {
    const openDdpId = location.state?.openDdpId;
    if (!openDdpId) {
      openDdpFromNavHandledRef.current = null;
      return;
    }
    if (openDdpFromNavHandledRef.current === openDdpId) return;
    openDdpFromNavHandledRef.current = openDdpId;

    navigate(location.pathname, { replace: true, state: {} });

    (async () => {
      try {
        setDdpModalLoading(true);
        const ddp = await ddpsAPI.getById(openDdpId);
        setSelectedDdp(ddp);
        setShowDdpModal(true);
      } catch (err) {
        toast.error(err?.response?.data?.error || err?.response?.data?.message || t('common.error'));
      } finally {
        setDdpModalLoading(false);
      }
    })();
  }, [location.state?.openDdpId, location.pathname, navigate, t, toast]);

  const canDeleteDdp = (row) => {
    if (!row) return false;
    if ((row._count?.loads || 0) > 0) return false;
    if (isAdmin()) return true;
    if (user?.role === 'MANAGER') return true;
    if (row.status !== 'pending') return false;
    return user?.role === 'CLIENT' && row.operatorId === user?.id;
  };

  const canEditDdp = (row) => {
    if (!row) return false;
    if (row.status === 'pending') return true;
    return user?.role === 'CLIENT' && row.operatorId === user?.id && row.status === 'denied';
  };

  const getStatusBadgeClass = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'denied':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-slate-100 text-slate-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const canMarkCompleted = (row) => {
    if (!row) return false;
    if (!['MANAGER', 'ADMIN'].includes(String(user?.role || '').toUpperCase())) return false;
    return String(row.status || '').toLowerCase() === 'accepted';
  };

  const handleMarkCompleted = async (row) => {
    if (!row?.id || !canMarkCompleted(row)) return;
    setCompletingId(row.id);
    try {
      await ddpsAPI.updateStatus(row.id, { status: 'completed' });
      toast.success(t('ddp.completeSuccess', { defaultValue: 'DDP marked as completed.' }));
      fetchDdps();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || t('common.error'));
    } finally {
      setCompletingId(null);
    }
  };

  const handleDeleteClick = (row) => {
    if (!canDeleteDdp(row)) return;
    setDdpToDelete(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ddpToDelete) return;
    setDeletingId(ddpToDelete.id);
    try {
      await ddpsAPI.delete(ddpToDelete.id);
      toast.success(t('ddp.deleteSuccess'));
      setDeleteModalOpen(false);
      setDdpToDelete(null);
      fetchDdps();
    } catch (err) {
      toast.error(err?.response?.data?.error || t('common.error'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setDdpToDelete(null);
  };

  const getDdpClientMeta = (ddp) => {
    const companyName =
      ddp?.operator?.assignedClient?.companyName ||
      ddp?.operator?.clientProfile?.companyName ||
      '';
    const clientId =
      ddp?.operator?.assignedClient?.id ||
      ddp?.operator?.clientProfile?.id ||
      ddp?.operatorId ||
      '';
    const fallbackName = `${ddp?.operator?.firstName || ''} ${ddp?.operator?.lastName || ''}`.trim() || ddp?.operator?.username || '—';
    return {
      id: String(clientId || ''),
      label: companyName || fallbackName
    };
  };

  // Default to pending records; allow showing all records via checkbox
  const statusFilteredDdps = showAllRecords
    ? ddps
    : ddps.filter((ddp) => String(ddp?.status || '').toLowerCase() !== 'completed');

  const clientFilterOptions = useMemo(() => {
    const byId = new Map();
    ddps.forEach((d) => {
      const meta = getDdpClientMeta(d);
      if (meta.id && !byId.has(meta.id)) {
        byId.set(meta.id, meta.label);
      }
    });
    return Array.from(byId.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  }, [ddps]);

  const clientFilteredDdps = selectedClientId
    ? statusFilteredDdps.filter((ddp) => getDdpClientMeta(ddp).id === selectedClientId)
    : statusFilteredDdps;

  // Filter DDPs based on search query
  const filteredDdps = searchQuery.trim() === ''
    ? clientFilteredDdps
    : clientFilteredDdps.filter(ddp => {
        const query = searchQuery.toLowerCase();
        return (
          (ddp.navire && ddp.navire.toLowerCase().includes(query)) ||
          (ddp.manutentionNumber && ddp.manutentionNumber.toLowerCase().includes(query)) ||
          (ddp.productsSummary && ddp.productsSummary.toLowerCase().includes(query))
        );
      });

  return (
    <div className="max-w-6xl mx-auto mt-8">
      <h2 className="text-xl font-bold mb-4">
        {user?.role === 'PESEUR' ? t('navigation.ddpPeseur') : t('ddp.listTitle')}
      </h2>
      
      <div className="flex flex-col gap-3 mb-4">
        {(user?.role === 'CLIENT' || user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'PESEUR') && (
        <Link
          to="/ddp/create"
          className="inline-flex items-center justify-center font-medium rounded-md px-4 py-2 text-sm min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 w-fit"
        >
          {t('ddp.createNew')}
        </Link>
        )}
        
        <input
          type="text"
          placeholder={`${t('common.search')} ${t('ddp.navire')}, ${t('ddp.manutentionNumber')}, ${t('ddp.products')}...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
        />

        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 w-fit cursor-pointer">
            <input
              type="checkbox"
              checked={showAllRecords}
              onChange={(e) => setShowAllRecords(e.target.checked)}
              className="checkbox-app"
            />
            <span>{t('ddp.showAllRecords')}</span>
          </label>
          <div className="ml-auto min-w-[16rem] w-[20rem]">
            <SearchableSelect
              inlineLabel
              label={t('ddp.clientName')}
              name="ddpListClientFilter"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(String(e.target.value || ''))}
              options={[
                {
                  value: '',
                  label: t('certificates.allClients', { defaultValue: 'Tous les clients' }),
                  searchText: t('certificates.allClients', { defaultValue: 'Tous les clients' })
                },
                ...clientFilterOptions
              ]}
              placeholder={t('certificates.allClients', { defaultValue: 'Tous les clients' })}
            />
          </div>
        </div>
      </div>

      <Table
        data={filteredDdps}
        loading={loading}
        emptyMessage={t('ddp.noRequests')}
        columns={[
          ...((isAdmin() || user?.role === 'MANAGER' || user?.role === 'PESEUR') ? [{
            Header: t('ddp.clientName'),
            accessor: 'operator',
            width: '160px',
            minWidth: '140px',
            render: (operator) => {
              if (!operator) return <span className="text-gray-400">—</span>;
              // Display client company name if available, otherwise show user full name
              if (operator.assignedClient?.companyName) {
                return operator.assignedClient.companyName;
              }
              if (operator.clientProfile?.companyName) {
                return operator.clientProfile.companyName;
              }
              return `${operator.firstName || ''} ${operator.lastName || ''}`.trim() || operator.username || '—';
            }
          }] : []),
          { Header: t('ddp.navire'), accessor: 'navire', width: '120px', minWidth: '100px' },
          {
            Header: t('ddp.port'),
            accessor: 'port',
            width: '120px',
            minWidth: '100px',
            render: (port) => port?.name || '—'
          },
          { Header: t('ddp.arrivingDate'), accessor: 'arrivingDate', width: '120px', minWidth: '110px' },
          {
            Header: t('ddp.transactionType'),
            accessor: 'transactionType',
            width: '110px',
            minWidth: '100px',
            render: (value) => value ? t(`loads.transactionTypes.${String(value).toLowerCase()}`) : '—'
          },
          { Header: t('ddp.products'), accessor: 'productsSummary', key: 'productsSummary', width: '200px', minWidth: '160px' },
          {
            Header: t('ddp.derivedTotalWeight'),
            accessor: 'totalWeight',
            width: '110px',
            minWidth: '100px',
            render: (value) => `${Number(value || 0).toLocaleString('fr-FR')} t`
          },
          { Header: t('ddp.manutentionNumber'), accessor: 'manutentionNumber', width: '150px', minWidth: '130px' },
          {
            Header: t('ddp.status'),
            accessor: 'status',
            width: '120px',
            minWidth: '110px',
            render: (value) => value ? (
              <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusBadgeClass(value)}`}>
                {t(`ddp.status.${value}`)}
              </span>
            ) : '—'
          },
          {
            Header: t('ddp.documents'),
            accessor: 'documentUrls',
            width: '220px',
            minWidth: '180px',
            render: (value) => {
              if (!value || typeof value !== 'string') return <span className="text-gray-400">—</span>;
              
              // Try to parse as JSON array first (new format with descriptions)
              let documents = [];
              try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) {
                  documents = parsed;
                } else {
                  // Fallback to comma-separated URLs (old format)
                  documents = value.split(',').map(u => ({ url: u.trim(), description: '' })).filter(d => d.url);
                }
              } catch {
                // Not JSON, treat as comma-separated URLs (old format)
                documents = value.split(',').map(u => ({ url: u.trim(), description: '' })).filter(d => d.url);
              }
              
              if (documents.length === 0) return <span className="text-gray-400">—</span>;
              
              return (
                <div className="flex flex-col gap-1">
                  {documents.map((doc, i) => {
                    let url = doc.url;
                    
                    // If URL is relative or incomplete, convert to full API URL
                    if (!url.startsWith('http') && !url.startsWith('blob:')) {
                      // Detect API base URL from current window location
                      const isHttps = window.location.protocol === 'https:';
                      const apiPort = isHttps ? '5443' : '5000';
                      const apiProtocol = isHttps ? 'https' : 'http';
                      url = `${apiProtocol}://localhost:${apiPort}${url.startsWith('/') ? url : '/' + url}`;
                    }
                    
                    const isBlob = url.startsWith('blob:');
                    if (isBlob) {
                      return (
                        <div key={i} className="text-gray-500 text-sm">
                          <span title={t('ddp.documentBlobUnavailable')}>
                            {t('ddp.documentLink', { index: i + 1 })}
                            <span className="text-gray-400 text-xs ml-0.5">({t('ddp.documentBlobUnavailable')})</span>
                          </span>
                          {doc.description && (
                            <div className="text-xs text-gray-400 ml-2">→ {doc.description}</div>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div key={i} className="text-sm">
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {t('ddp.documentLink', { index: i + 1 })}
                        </a>
                        {doc.description && (
                          <span className="text-xs text-gray-600 ml-2">→ {doc.description}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            },
          },
          {
            key: 'actions',
            title: t('common.actions'),
            Header: t('common.actions'),
            accessor: 'id',
            sticky: true,
            width: '280px',
            minWidth: '280px',
            render: (_, row) => {
              return (
                <div className="flex flex-nowrap gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/ddp/${row.id}`)}
                    title={t('common.view')}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Button>
                  {canEditDdp(row) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/ddp/${row.id}`)}
                      title={t('common.edit')}
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canDeleteDdp(row) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteClick(row)}
                      disabled={deletingId === row.id}
                      title={t('common.delete')}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canMarkCompleted(row) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMarkCompleted(row)}
                      disabled={completingId === row.id}
                      title={t('ddp.markCompleted', { defaultValue: 'Mark completed' })}
                      className="text-[11px] leading-tight px-2 py-1 border-slate-300 bg-slate-100 text-slate-800 hover:bg-slate-200"
                    >
                      {t('ddp.markCompleted', { defaultValue: 'Mark completed' })}
                    </Button>
                  )}
                </div>
              );
            },
          },
        ]}
      />

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('ddp.confirmDeleteTitle')}
        message={t('ddp.confirmDeleteMessage', { navire: ddpToDelete?.navire || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        loading={!!deletingId}
        variant="danger"
      />

      <Modal
        isOpen={showDdpModal}
        onClose={() => {
          setShowDdpModal(false);
          setSelectedDdp(null);
        }}
        title={t('ddp.viewDetails')}
        size="lg"
      >
        {ddpModalLoading ? (
          <div className="py-6 text-sm text-gray-500">{t('common.loading')}...</div>
        ) : selectedDdp ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">{t('ddp.manutentionNumber')}</p>
                <p className="font-mono text-gray-900">{selectedDdp.manutentionNumber || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('ddp.status')}</p>
                <p className="text-gray-900">{selectedDdp.status ? t(`ddp.status.${selectedDdp.status}`) : '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('ddp.navire')}</p>
                <p className="text-gray-900">{selectedDdp.navire || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('ddp.arrivingDate')}</p>
                <p className="text-gray-900">{selectedDdp.arrivingDate || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('ddp.port')}</p>
                <p className="text-gray-900">{selectedDdp.port?.name || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('ddp.transactionType')}</p>
                <p className="text-gray-900">
                  {selectedDdp.transactionType
                    ? t(`loads.transactionTypes.${String(selectedDdp.transactionType).toLowerCase()}`)
                    : '—'}
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-1">{t('ddp.products')}</p>
              <p className="text-sm text-gray-900">{selectedDdp.productsSummary || '—'}</p>
            </div>

            <div className="flex justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setShowDdpModal(false)}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-6 text-sm text-gray-500">{t('common.noData')}</div>
        )}
      </Modal>
    </div>
  );
};

export default DDPListPage;
